// Timestop BO — group-level Accounting charts.
//
// Phase 12.14b: three charts below the group Accounting table, mirroring
// the per-terminal Ranged charts in accounting-ranged.ts but driven by
// /api/groupacc's per-day aggregates.
//
// The data shape coming out of /api/groupacc is identical to the
// per-terminal Ranged cache (each record has acc_diff: {in:{coin,note,
// card}, out:[{cnt,ISO},...]} for a single YYYY-MM-DD), so the
// chart-builder functions are the same — kept in their own module
// to avoid coupling the group page to per-terminal internals.
//
// What this file adds on top of the per-terminal charts:
//   - Active terminals per day: a single-series line chart of
//     mid_count over the range. mid_count is unique to /api/groupacc
//     (the per-mid breakdown is collapsed server-side), and it's the
//     only "per-mid" data the server hands us at the group level, so
//     we chart it as-is.
//
// Chart lifecycle: instances are kept in a module-scope list and
// destroyed on every re-render. Same pattern as the per-terminal
// Ranged tab — preventing Chart.js from leaking canvas + listeners
// across group / range / nav changes.

import type { NormalisedAcc } from './accounting';
import type { Terminal } from './types';
import { t as i18n } from './i18n';

// Chart instance list, used so we can destroy prior instances on
// the next re-render. The list is module-scope (per page) — the
// group page never coexists with the per-terminal Ranged tab, so
// a separate registry is fine.
const _groupCharts: Array<{ destroy: () => void }> = [];

export function destroyGroupCharts(): void {
  while (_groupCharts.length) {
    const c = _groupCharts.pop();
    try { c && c.destroy(); } catch { /* already gone */ }
  }
}

type ChartCtor = new (ctx: HTMLCanvasElement, config: Record<string, unknown>) => { destroy: () => void };
function getChartCtor(): ChartCtor | null {
  const w = window as unknown as { Chart?: ChartCtor };
  return typeof w.Chart === 'function' ? w.Chart : null;
}

// Same palette as the per-terminal charts so the two pages look
// like they belong to the same product.
const SERIES_COLORS = [
  '#10b981', // emerald-500
  '#3b82f6', // blue-500
  '#f59e0b', // amber-500
  '#8b5cf6', // violet-500
  '#ef4444', // red-500
  '#06b6d4', // cyan-500
];

function shortDate(ymd: string): string { return ymd.slice(5); }

// ---- Series builders (data-only; no DOM, no Chart) ----
//
// Each returns the {labels, datasets} shape Chart.js expects. Pure
// functions of the inputs so they're trivial to unit-test if we
// ever add a test suite.

// Series-builder return type. We declare only the keys we set
// ourselves; Chart.js accepts any extras (fill, stepped, etc.) on
// each dataset, but we don't need to model them statically. If
// the strict shape ever matters, this can be tightened.
type LineDataset = { label: string; data: number[]; borderColor: string; backgroundColor: string; tension: number; pointRadius: number; pointHoverRadius: number; borderWidth: number; [k: string]: unknown };
type ChartLineData = { labels: string[]; datasets: LineDataset[] };

export function buildMoneyInSeries(
  accs: NormalisedAcc[],
  dates: string[],
  isos: string[],
): ChartLineData {
  const labels = dates.map(shortDate);
  const datasets = isos.map((iso, i) => {
    const data: number[] = [];
    for (let d = 0; d < accs.length; d++) {
      const a = accs[d];
      // Money in = coins + notes (no cards on the cabinet).
      const c = Number(a?.in?.coin?.[iso] || 0);
      const n = Number(a?.in?.note?.[iso] || 0);
      data.push(c + n);
    }
    const color = SERIES_COLORS[i % SERIES_COLORS.length];
    return {
      label: iso,
      data,
      borderColor: color,
      backgroundColor: color,
      tension: 0.25,
      // A single-day range is one point: with radius 0 the line
      // renders as an empty plot, so show the dot when short.
      pointRadius: data.length <= 1 ? 4 : 0,
      pointHoverRadius: 5,
      borderWidth: 2,
    };
  });
  return { labels, datasets };
}

export function buildGamesCountSeries(
  accs: NormalisedAcc[],
  dates: string[],
): ChartLineData {
  const labels = dates.map(shortDate);
  const datasets = [0, 1, 2].map((idx) => {
    const data: number[] = [];
    for (let d = 0; d < accs.length; d++) {
      data.push(Number(accs[d]?.games?.count?.[idx] || 0));
    }
    const color = SERIES_COLORS[idx];
    return {
      label: i18n('tab_accounting.game_n', { n: (idx + 1) }),
      data,
      borderColor: color,
      backgroundColor: color,
      tension: 0.25,
      // A single-day range is one point: with radius 0 the line
      // renders as an empty plot, so show the dot when short.
      pointRadius: data.length <= 1 ? 4 : 0,
      pointHoverRadius: 5,
      borderWidth: 2,
    };
  });
  return { labels, datasets };
}

// Build a per-terminal games-played bar series (HORIZONTAL bar chart).
// One bar per terminal, length = games played across the whole range
// (sum of the three game slots over every day).
export function buildPerTerminalGamesBarSeries(
  gamesByMids: Record<string, number[]>[],
  terminals: Terminal[],
): { labels: string[]; datasets: Array<{ data: number[]; backgroundColor: string[]; borderColor: string; borderWidth: number }> } {
  const labels: string[] = [];
  const data: number[] = [];
  const colors: string[] = [];
  for (let i = 0; i < terminals.length; i++) {
    const term = terminals[i];
    const termId = String(term.id);
    let total = 0;
    for (let d = 0; d < gamesByMids.length; d++) {
      const slots = gamesByMids[d][termId];
      if (!Array.isArray(slots)) continue;
      for (const n of slots) total += Number(n || 0);
    }
    labels.push((term.name || term.location || i18n('common.terminal') + ' ' + termId) + ` (#${termId})`);
    data.push(total);
    colors.push(SERIES_COLORS[i % SERIES_COLORS.length]);
  }
  return {
    labels,
    datasets: [{
      data,
      backgroundColor: colors,
      borderColor: '#ffffff',
      borderWidth: 1,
    }],
  };
}

export function buildGamesPieData(
  accs: NormalisedAcc[],
): { labels: string[]; datasets: Array<{ data: number[]; backgroundColor: string[]; borderColor: string; borderWidth: number }> } {
  const totals = [0, 0, 0];
  for (const a of accs) {
    for (let i = 0; i < 3; i++) {
      totals[i] += Number(a?.games?.count?.[i] || 0);
    }
  }
  return {
    labels: [
      i18n('tab_accounting.game_with_count', { n: 1, count: totals[0] }),
      i18n('tab_accounting.game_with_count', { n: 2, count: totals[1] }),
      i18n('tab_accounting.game_with_count', { n: 3, count: totals[2] }),
    ],
    datasets: [{
      data: totals,
      backgroundColor: SERIES_COLORS.slice(0, 3),
      borderColor: '#ffffff',
      borderWidth: 2,
    }],
  };
}

// Active-terminals series — uses mid_count (the per-day distinct
// terminal count), unique to /api/groupacc. Single-series line
// chart.
export function buildActiveTerminalsSeries(
  midCounts: number[],
  dates: string[],
): ChartLineData {
  return {
    labels: dates.map(shortDate),
    datasets: [{
      label: i18n('accounting_group.active_terminals_label'),
      data: midCounts.map((n) => Number(n || 0)),
      borderColor: SERIES_COLORS[3], // violet-500
      backgroundColor: SERIES_COLORS[3],
      tension: 0.25,
      pointRadius: 1,
      pointHoverRadius: 4,
      borderWidth: 2,
      fill: false,
    }],
  };
}

// ---- DOM painters ----

function paintChart(
  parent: HTMLElement,
  ctor: ChartCtor,
  type: 'line' | 'bar' | 'pie',
  data: unknown,
  options: Record<string, unknown>,
  height: number,
): HTMLCanvasElement {
  const c = document.createElement('canvas');
  c.style.width = '100%';
  c.style.height = height + 'px';
  c.style.maxHeight = height + 'px';
  parent.appendChild(c);
  const inst = new ctor(c, { type, data, options });
  _groupCharts.push(inst);
  return c;
}

// Public entry: paint the 4 charts inside `parent`. Caller is
// responsible for clearing `parent` before calling this.
//
// `accs`    : the list of NormalisedAcc pulled from /api/groupacc,
//             one per day in the picked range.
// `dates`   : matching YYYY-MM-DD strings (the group's `dated`
//             column for each acc). Same length as accs.
// `midCounts`: per-day mid_count (from /api/groupacc's mid_count
//             field), one per acc. Same length as accs.
export function renderGroupCharts(
  parent: HTMLElement,
  accs: NormalisedAcc[],
  dates: string[],
  midCounts: number[],
  gamesByMids: Record<string, number[]>[],
  terminals: Terminal[],
): void {
  if (accs.length === 0) return;
  const ctor = getChartCtor();
  if (!ctor) {
    // Defensive: Chart.js didn't load (e.g. CDN blocked). Surface
    // a one-line notice so the user knows the charts are missing
    // rather than seeing a silent empty space.
    const note = document.createElement('div');
    note.className = 'bg-gray-100 rounded-2xl p-3 text-xs text-gray-400 text-center';
    note.textContent = i18n('tab_accounting.charts_unavailable');
    parent.appendChild(note);
    return;
  }

  // ISOs in use across the range. Built from accs so currencies
  // that exist on some terminal but had no activity in the range
  // don't get an empty line. Kept here so a future "money in"
  // card (e.g. behind a toggle) can re-use the union without
  // recomputing. The actual Money In card is no longer painted
  // on this page per the user's request.
  const isos = new Set<string>();
  for (const a of accs) {
    for (const ch of ['coin', 'note'] as const) {
      const m = a?.in?.[ch] || {};
      for (const iso of Object.keys(m)) isos.add(iso);
    }
  }
  // (isoList intentionally unused here; left as a hint for future
  // cards that may want the same union.)
  void Array.from(isos);

  // Card 2: Token out per terminal — HORIZONTAL BAR chart, one
  // bar per terminal, length = sum of the 3 hopper counts for
  // that terminal across every day in the range. Replaces the
  // previous per-terminal LINE chart. "Swap its orientation"
  // means the terminals are now the category axis (Y), the
  // count is the value axis (X), so long terminal names still
  // read naturally and the bar length carries the magnitude.
  // Skipped if the group has no terminals (a freshly imported
  // group is a real edge case).
  const card2 = document.createElement('div');
  card2.className = 'bg-white border rounded-2xl p-4';
  const h2 = document.createElement('div');
  h2.className = 'text-sm font-semibold text-gray-700 mb-2';
  h2.textContent = i18n('accounting_group.card_games_per_terminal');
  card2.appendChild(h2);
  if (terminals.length > 0) {
    paintChart(
      card2,
      ctor,
      'bar',
      buildPerTerminalGamesBarSeries(gamesByMids, terminals),
      {
        // indexAxis: 'y' flips the bar chart to horizontal.
        // X (value) axis: count. Y (category) axis: terminal
        // name. Ticks on X are integers; Y labels can wrap.
        indexAxis: 'y',
        plugins: { legend: { display: false } },
        scales: {
          x: { beginAtZero: true, grid: { color: '#f1f5f9' }, ticks: { precision: 0 } },
          y: { grid: { display: false } },
        },
        responsive: true,
        maintainAspectRatio: false,
      },
      // Height grows with terminal count so a 10-terminal group
      // doesn't squash the labels.
      Math.max(180, 40 + terminals.length * 28),
    );
  } else {
    const note = document.createElement('div');
    note.className = 'text-xs text-gray-400 text-center';
    note.textContent = i18n('accounting_group.no_terminals_in_group');
    card2.appendChild(note);
  }
  parent.appendChild(card2);

  // Card 2b: Token out (per hopper, per terminals) — STACKED bar
  // chart. Same per-day, per-hopper series as the per-terminal
  // Ranged tab's stacked bar. Each day's 3 hopper values stack
  // into a single bar; total height = total tokens out that day.
  // The per-day totals are already summed across every mid by
  // /api/groupacc, so the bar shows the group's aggregate per day.
  const card2b = document.createElement('div');
  card2b.className = 'bg-white border rounded-2xl p-4';
  const h2b = document.createElement('div');
  h2b.className = 'text-sm font-semibold text-gray-700 mb-2';
  h2b.textContent = i18n('accounting_group.card_games_per_slot');
  card2b.appendChild(h2b);
  paintChart(
    card2b,
    ctor,
    'bar',
    buildGamesCountSeries(accs, dates),
    {
      plugins: { legend: { position: 'bottom', labels: { boxWidth: 12 } } },
      scales: {
        x: { stacked: true, grid: { color: '#f1f5f9' } },
        y: { stacked: true, beginAtZero: true, grid: { color: '#f1f5f9' }, ticks: { precision: 0 } },
      },
      responsive: true,
      maintainAspectRatio: false,
    },
    220,
  );
  parent.appendChild(card2b);

  // Card 4: Active terminals per day — single-series line, uses
  // mid_count from /api/groupacc. Skipped if all zeros (a freshly
  // registered group with no terminal activity yet).
  const hasAnyMid = midCounts.some((n) => Number(n || 0) > 0);
  if (hasAnyMid) {
    const card4 = document.createElement('div');
    card4.className = 'bg-white border rounded-2xl p-4';
    const h4 = document.createElement('div');
    h4.className = 'text-sm font-semibold text-gray-700 mb-2';
    h4.textContent = i18n('accounting_group.card_active_terminals_per_day');
    card4.appendChild(h4);
    paintChart(
      card4,
      ctor,
      'line',
      buildActiveTerminalsSeries(midCounts, dates),
      {
        plugins: { legend: { display: false } },
        scales: {
          y: { beginAtZero: true, grid: { color: '#f1f5f9' }, ticks: { precision: 0 } },
          x: { grid: { color: '#f1f5f9' } },
        },
        responsive: true,
        maintainAspectRatio: false,
      },
      180,
    );
    parent.appendChild(card4);
  }
}
